import React, {
  useCallback,
  useState,
  useMemo,
  useEffect,
  useRef,
} from "react";
import {
  Pressable,
  TextInput,
  View,
  FlatList,
  ActivityIndicator,
  Linking,
  Image,
} from "react-native";
import PageTemplate from "../components/common/page-template";
import StyledText from "../components/common/ui/styled-text";
import { router, useFocusEffect, useLocalSearchParams } from "expo-router";
import { useAudioPlayer } from "../contexts/AudioPlayerContext";
import { useActiveAgent } from "../contexts/ActiveAgentContext";
import { useCallsService } from "../services/api/calls-service";
import { useInfiniteQuery } from "@tanstack/react-query";
import { ICall, IReportCallData } from "../types/call";
import { useAuth } from "@clerk/clerk-react";
import { usePostHog } from "posthog-js/react";
import { ErrorDisplay } from "../components/common/error-display";
import ReportCallModal from "../components/feature/logs/report-call-modal";
import RefundModal from "../components/feature/logs/refund-modal";
import Trackbar from "../components/feature/logs/trackbar";
import { debounce } from "lodash";
import CallsFilter from "../components/calls-filter";
import dayjs from "../utils/dayjs";
import CallCard from "../components/call-card";
import CallCardSeparator from "../components/feature/logs/call-card-separator";
import { formatPhoneNumber } from "react-phone-number-input";
import { displayPrice } from "../utils/price-helpers";
import { Badge } from "../components/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipProvider,
} from "../components/common/shadcn/tooltip";
import {
  Table,
  TableBody,
  TableHeader,
  TableRow,
  TableHead,
  TableCell,
} from "../components/common/shadcn/table";
import CallDetailsPanel from "../components/feature/logs/call-details-panel";
import { ICallOrderItem } from "../types/order";
import { isStreamOrderingAgent } from "../utils/agent-helpers";
import {
  formatDuration,
  isCallProcessing,
  isCallCompleted,
  hasBeenRefunded,
  isExpired,
  isOrderSubmittedViaTablet,
  isVoided,
  isCallWithOrder,
  isCallOngoing,
  filterValidCalls,
} from "../utils/call-helpers";
import { useSettingsData } from "../contexts/SettingsDataContext";
import LoadingView from "../components/common/loading-view";
import { useFormattedDate } from "../hooks/useFormattedDate";
import { Phone, Search, X } from "lucide-react-native";
import { useCallersService } from "../services/api/callers-service";
import { E164Number } from "../types/agent-settings";
import {
  OrderCallIconWithStatus,
  PhoneCallIcon,
  ProcessingCallIcon,
} from "../components/call-display-icons";
import { useViewport } from "../hooks/useViewport";

const liveIcon = require("../assets/live.svg");

const SHOW_ORDERS_ONLY_KEY = "loman_calls_orders_filter";

const callOrderItemsSummaryText = (call: ICall) => {
  const items = call.order?.items || [];
  if (items.length === 0) return "";
  const maxItemsToShow = 2;
  if (items.length <= maxItemsToShow) {
    return items.map((item) => `• ${item.quantity}x ${item.name}`).join("\n");
  } else {
    const firstThree = items
      .slice(0, maxItemsToShow)
      .map((item) => `• ${item.quantity}x ${item.name}`)
      .join("\n");
    const remainingCount = items.length - maxItemsToShow;
    return `${firstThree}\n+ ${remainingCount} more item${
      remainingCount > 1 ? "s" : ""
    }`;
  }
};

const CallsAndOrders = () => {
  const { getToken } = useAuth();
  const { activeAgent } = useActiveAgent();
  const posthog = usePostHog();
  const params = useLocalSearchParams();
  const { formatTime, formatDateShort, formatDateTime, formatDateLong } =
    useFormattedDate();
  const callsService = useCallsService();

  const { handleCloseTrackbar } = useAudioPlayer();

  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [showOrdersOnly, setShowOrdersOnly] = useState(() => {
    return localStorage.getItem(SHOW_ORDERS_ONLY_KEY) === "true";
  });
  const [expandedItemIds, setExpandedItemIds] = useState<Set<string>>(
    new Set()
  );
  const [dateRange, setDateRange] = useState<
    { from: Date; to: Date } | undefined
  >(undefined);

  const [reportCallModalVisible, setReportCallModalVisible] = useState(false);
  const [selectedCallId, setSelectedCallId] = useState<string | null>(null);
  const [selectedCallTimestamp, setSelectedCallTimestamp] = useState<
    string | null
  >(null);

  const [refundModalVisible, setRefundModalVisible] = useState(false);
  const [selectedCallForRefund, setSelectedCallForRefund] =
    useState<ICall | null>(null);
  const [refundLoading, setRefundLoading] = useState(false);
  const [refundSuccess, setRefundSuccess] = useState(false);
  const [refundError, setRefundError] = useState<string | null>(null);

  // Desktop side panel state
  const [selectedCallForPanel, setSelectedCallForPanel] =
    useState<ICall | null>(null);
  const [isSidePanelOpen, setIsSidePanelOpen] = useState(false);
  const [isPanelAnimating, setIsPanelAnimating] = useState(false);

  const { isDesktop } = useViewport();

  const flatListRef = useRef<FlatList>(null);
  const tableContainerRef = useRef<HTMLDivElement>(null);

  const { settings, isLoading: isSettingsLoading } = useSettingsData();
  const isStreamOrdering = useMemo(
    () =>
      !isSettingsLoading &&
      isStreamOrderingAgent(
        activeAgent?.type,
        settings?.posProvider === "stream"
      ),
    [activeAgent?.type, settings?.posProvider, isSettingsLoading]
  );

  // useFocusEffect(
  //   useCallback(() => {
  //     // hide crisp chatbox on logs page
  //     document.body.setAttribute("data-page", "logs");
  //     return async () => {
  //       document.body.removeAttribute("data-page");
  //       await handleCloseTrackbar();
  //     };
  //   }, [activeAgent, params.callId])
  // );

  useEffect(() => {
    const debouncedSetSearch = debounce((value: string) => {
      setDebouncedSearchQuery(value);
    }, 700);
    debouncedSetSearch(searchQuery);
    return () => {
      debouncedSetSearch.cancel();
    };
  }, [searchQuery]);

  // Save showOrdersOnly filter to localStorage when it changes
  useEffect(() => {
    localStorage.setItem(SHOW_ORDERS_ONLY_KEY, showOrdersOnly.toString());
  }, [showOrdersOnly]);

  // reset expanded item ids when filters change
  useEffect(() => {
    setExpandedItemIds(new Set());
  }, [debouncedSearchQuery, dateRange, showOrdersOnly]);

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetching,
    error,
  } = useInfiniteQuery<{
    calls: ICall[];
    nextCursor: string | null;
  }>({
    queryKey: [
      "logs",
      activeAgent?.id,
      debouncedSearchQuery,
      dateRange,
      showOrdersOnly,
    ],
    queryFn: async ({ pageParam = null }) => {
      if (!activeAgent) return { calls: [], nextCursor: null };

      const params = new URLSearchParams({
        pageSize: "20",
        ...(pageParam ? { cursor: pageParam as string } : {}),
        ...(debouncedSearchQuery?.length > 0
          ? {
              searchQuery:
                debouncedSearchQuery.length >= 3 &&
                (/^\d{3}/.test(debouncedSearchQuery) ||
                  /^\(\d{3}\)/.test(debouncedSearchQuery))
                  ? debouncedSearchQuery.replace(/\D/g, "")
                  : debouncedSearchQuery,
            }
          : {}),
        ...(dateRange
          ? {
              startDate: dayjs(dateRange.from)
                .hour(0)
                .minute(0)
                .second(0)
                .millisecond(0)
                .tz(activeAgent.timezone)
                .utc()
                .toISOString(),
              endDate: dayjs(dateRange.to)
                .hour(23)
                .minute(59)
                .second(0)
                .millisecond(0)
                .tz(activeAgent.timezone)
                .utc()
                .toISOString(),
            }
          : {}),
        ...(showOrdersOnly ? { hasOrder: "true" } : {}),
      });
      const response = await fetch(
        `${process.env.EXPO_PUBLIC_API_BASE}/agents/${activeAgent.id}/calls?${params}`,
        {
          headers: {
            Authorization: `Bearer ${await getToken()}`,
          },
        }
      );
      if (!response.ok) {
        throw new Error(`Response status: ${response.status}`);
      }
      return await response.json();
    },
    getNextPageParam: (lastPage) => {
      return lastPage.nextCursor;
    },
    initialPageParam: null,
    enabled: !!activeAgent,
    refetchInterval: 1 * 60 * 1000, // 1 minute
    refetchOnWindowFocus: false,
  });

  const callLogs = useMemo(() => {
    if (!data) return [];
    const allCalls = data.pages.flatMap((page) => page.calls);
    return filterValidCalls(allCalls);
  }, [data]);

  const combinedData = useMemo(() => {
    if (!callLogs.length) return { items: [] };
    const result: Array<{ type: "separator" | "call"; data: any; id: string }> =
      [];
    const today = dayjs().format("dddd, MMMM Do");
    callLogs.forEach((call, index) => {
      const currentDate = formatDateLong(call.timestamp);
      const isToday = currentDate === today;
      const previousItem = index > 0 ? callLogs[index - 1] : null;
      const previousDate = previousItem
        ? formatDateLong(previousItem.timestamp)
        : null;
      const previousIsToday = previousDate === today;
      // Show separator only when transitioning from today to past calls
      const showDateSeparator = !isToday && previousIsToday;
      if (showDateSeparator) {
        result.push({
          type: "separator",
          data: { currentDate },
          id: `separator-${currentDate}`,
        });
      }
      result.push({
        type: "call",
        data: call,
        id: call.id,
      });
    });
    return { items: result };
  }, [callLogs]);

  const hasActiveFilters = useMemo(() => {
    return showOrdersOnly || !!dateRange;
  }, [showOrdersOnly, dateRange]);

  const hasSearchQuery = useMemo(() => {
    return debouncedSearchQuery.length > 0;
  }, [debouncedSearchQuery]);

  const flatListKey = useMemo(() => {
    return JSON.stringify({
      search: debouncedSearchQuery,
      dateRange: dateRange
        ? {
            from: dateRange.from.toISOString(),
            to: dateRange.to.toISOString(),
          }
        : null,
      showOrdersOnly,
    });
  }, [debouncedSearchQuery, dateRange, showOrdersOnly]);

  // Reset search and date range filter when the agent changes
  useEffect(() => {
    setSearchQuery("");
    setDebouncedSearchQuery("");
    setDateRange(undefined);
    setExpandedItemIds(new Set());
    setSelectedCallForPanel(null);
    setIsSidePanelOpen(false);
    setIsPanelAnimating(false);
  }, [activeAgent?.id]);

  // open specific call on page load
  useEffect(() => {
    const callId = params.callId as string;
    if (callId && callLogs.length > 0 && !isFetching) {
      const callExists = callLogs.some((call) => call.id === callId);
      if (callExists) {
        const call = callLogs.find((call) => call.id === callId);
        if (call) {
          if (isDesktop) {
            setSelectedCallForPanel(call);
            setIsSidePanelOpen(true);
            setIsPanelAnimating(false);
          } else {
            setExpandedItemIds(new Set([callId]));
            const itemIndex = combinedData.items.findIndex(
              (item) => item.type === "call" && item.data.id === callId
            );
            if (itemIndex !== -1) {
              setTimeout(() => {
                flatListRef.current?.scrollToIndex({
                  index: itemIndex,
                  animated: true,
                  viewPosition: 0,
                  viewOffset: 45,
                });
              }, 100);
            }
          }
        }
      }
      // Clear the URL param after handling it
      router.replace("/calls-and-orders");
    }
  }, [callLogs, isFetching, params.callId, combinedData.items, isDesktop]);

  const handleLoadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  // Desktop table scroll handler for infinite loading
  const handleTableScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      const target = event.target as HTMLDivElement;
      const { scrollTop, scrollHeight, clientHeight } = target;

      // Load more when user scrolls to within 100px of the bottom
      if (scrollHeight - scrollTop - clientHeight < 100) {
        handleLoadMore();
      }
    },
    [handleLoadMore]
  );

  const handleOpenReportCallModal = useCallback(
    (callId: string, callTimestamp: string) => {
      posthog.capture("report_call_modal_opened", {
        call_id: callId,
      });
      setSelectedCallId(callId);
      setSelectedCallTimestamp(callTimestamp);
      setReportCallModalVisible(true);
    },
    []
  );

  const callersService = useCallersService();
  const [blockedCallers, setBlockedCallers] = useState<E164Number[]>([]);
  const [blockingPhone, setBlockingPhone] = useState<E164Number | null>(null);

  // Fetch blocked callers
  useEffect(() => {
    if (activeAgent?.id) {
      callersService.fetchBlockedCallers().then(setBlockedCallers);
    }
  }, [activeAgent?.id]);

  const isCallerBlocked = useCallback(
    (phone: E164Number) => {
      return blockedCallers.includes(phone);
    },
    [blockedCallers]
  );

  const isBlockUnblockLoading = useCallback(
    (phone: E164Number) => {
      return blockingPhone === phone;
    },
    [blockingPhone]
  );

  const handleBlockCaller = useCallback(
    async (callId: string, phone: E164Number) => {
      posthog.capture("caller_blocked", {
        call_id: callId,
        phone: phone,
      });
      setBlockingPhone(phone);
      try {
        await callersService.addBlockedCaller(phone);
        setBlockedCallers([...blockedCallers, phone]);
      } catch (error) {
        console.error("Failed to block caller:", error);
      } finally {
        setBlockingPhone(null);
      }
    },
    [callersService, blockedCallers]
  );

  const handleUnblockCaller = useCallback(
    async (callId: string, phone: E164Number) => {
      posthog.capture("caller_unblocked", {
        call_id: callId,
        phone: phone,
      });
      setBlockingPhone(phone);
      try {
        await callersService.deleteBlockedCaller(phone);
        setBlockedCallers(blockedCallers.filter((p) => p !== phone));
      } catch (error) {
        console.error("Failed to unblock caller:", error);
      } finally {
        setBlockingPhone(null);
      }
    },
    [callersService, blockedCallers]
  );

  const handleCloseReportCallModal = useCallback(() => {
    posthog.capture("report_call_modal_closed", {
      call_id: selectedCallId,
    });
    setReportCallModalVisible(false);
    setSelectedCallId(null);
    setSelectedCallTimestamp(null);
  }, []);

  const handleOpenRefundModal = useCallback((call: ICall) => {
    posthog.capture("refund_modal_opened", {
      call_id: call.id,
    });
    setSelectedCallForRefund(call);
    setRefundModalVisible(true);
  }, []);

  const handleCloseRefundModal = useCallback(() => {
    posthog.capture("refund_modal_closed", {
      call_id: selectedCallForRefund?.id,
    });
    setRefundModalVisible(false);
    setSelectedCallForRefund(null);
    setRefundLoading(false);
    setRefundSuccess(false);
    setRefundError(null);
  }, [selectedCallForRefund?.id]);

  const handleRefundSubmit = useCallback(
    async (amount: number) => {
      posthog.capture("refund_button_pressed", {
        call_id: selectedCallForRefund?.id,
        order_id: selectedCallForRefund?.order?.id,
        amount: amount,
      });
      if (!selectedCallForRefund || !selectedCallForRefund.order) return;

      if (amount < 1) {
        setRefundError("Refund amount is too low");
        return;
      }

      if (amount > selectedCallForRefund.order.total) {
        setRefundError("Amount cannot be greater than the order total");
        return;
      }

      setRefundLoading(true);
      setRefundError(null);
      try {
        const orderId = selectedCallForRefund.order.id;
        if (!orderId) {
          throw new Error("No order ID found for refund");
        }
        const response = await fetch(
          `${process.env.EXPO_PUBLIC_API_BASE}/orders/${orderId}/refund`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${await getToken()}`,
            },
            body: JSON.stringify({
              amount: Math.floor(amount),
            }),
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        await response.json();
        setRefundSuccess(true);
        setRefundLoading(false);

        posthog.capture("refund_submitted", {
          call_id: selectedCallForRefund?.id,
          order_id: orderId,
          amount: amount,
        });
      } catch (error) {
        console.error("Refund API error:", error);
        setRefundLoading(false);
        setRefundError(
          error instanceof Error
            ? error.message
            : "An unexpected error occurred while processing your refund. Please try again."
        );
      }
    },
    [selectedCallForRefund?.id]
  );

  const handleCallBack = useCallback((callId: string, phone: string) => {
    posthog.capture("call_back_pressed", {
      call_id: callId,
    });
    Linking.openURL(`tel:${phone}`);
  }, []);

  const handleFilterFromNumber = useCallback(
    (callId: string, phone: string) => {
      posthog.capture("filter_from_number_pressed", {
        call_id: callId,
        phone: phone,
      });
      setSearchQuery(phone);
    },
    []
  );

  const handleReportCallSubmit = useCallback(
    async (data: IReportCallData) => {
      posthog.capture("report_call_submitted", {
        call_id: selectedCallId,
        topic: data.topic,
      });
      if (!selectedCallId) return;
      await callsService.reportIssue(selectedCallId, data);
    },
    [selectedCallId, callsService]
  );

  const handleClearFilter = useCallback(() => {
    posthog.capture("filter_from_number_cleared");
    setSearchQuery("");
  }, []);

  const handleSearchQueryChange = useCallback((text: string) => {
    setSearchQuery(text);
  }, []);

  const handleToggleExpand = (id: string) => {
    setExpandedItemIds((prevIds) => {
      const newIds = new Set(prevIds);
      if (newIds.has(id)) {
        newIds.delete(id);
      } else {
        newIds.add(id);
        // If we're expanding an item, scroll it to the top
        const itemIndex = combinedData.items.findIndex(
          (item) => item.id === id
        );
        if (itemIndex !== -1) {
          setTimeout(() => {
            flatListRef.current?.scrollToIndex({
              index: itemIndex,
              animated: true,
              viewPosition: 0,
              viewOffset: 45,
            });
          }, 80);
        }
      }
      return newIds;
    });
  };

  const getItemCount = (items: ICallOrderItem[]) => {
    if (items == null) return <span className="text-neutral-400">-</span>;
    const count = items.reduce((acc, item) => acc + item.quantity, 0);
    return (
      <p>
        {count} item{count > 1 ? "s" : ""}
      </p>
    );
  };

  // Desktop table row click handler
  const handleTableRowClick = useCallback(
    (call: ICall) => {
      // If the same call is already selected, deselect it and close the panel
      if (selectedCallForPanel?.id === call.id) {
        setIsPanelAnimating(true);
        setIsSidePanelOpen(false);
        setTimeout(() => {
          setSelectedCallForPanel(null);
          setIsPanelAnimating(false);
        }, 400);
      } else {
        // Otherwise, select the new call and open the panel
        setSelectedCallForPanel(call);
        setIsSidePanelOpen(true);
        setIsPanelAnimating(false);
      }
    },
    [selectedCallForPanel?.id]
  );

  // Close side panel
  const handleCloseSidePanel = useCallback(() => {
    setIsPanelAnimating(true);
    setIsSidePanelOpen(false);
    // Delay clearing the selected call to allow exit animation to complete
    setTimeout(() => {
      setSelectedCallForPanel(null);
      setIsPanelAnimating(false);
    }, 400); // Slightly longer than transition to ensure animation completes
  }, []);

  const renderItem = useCallback(
    ({
      item,
    }: {
      item: { type: "separator" | "call"; data: any; id: string };
    }) => {
      if (item.type === "separator") {
        return <CallCardSeparator />;
      }

      const call = item.data as ICall;
      return (
        <CallCard
          call={call}
          isExpanded={expandedItemIds.has(call.id)}
          onToggle={() => handleToggleExpand(call.id)}
          onViewHistory={() => handleFilterFromNumber(call.id, call.phone)}
          onReportCall={() =>
            handleOpenReportCallModal(call.id, call.timestamp)
          }
          onCallBack={() => handleCallBack(call.id, call.phone)}
          onRefund={() => handleOpenRefundModal(call)}
          isStreamOrdering={isStreamOrdering}
          onBlockCaller={(phone) => handleBlockCaller(call.id, phone)}
          onUnblockCaller={(phone) => handleUnblockCaller(call.id, phone)}
          isCallerBlocked={isCallerBlocked}
          isBlockUnblockLoading={isBlockUnblockLoading}
        />
      );
    },
    [
      expandedItemIds,
      handleFilterFromNumber,
      handleOpenReportCallModal,
      handleCallBack,
      handleOpenRefundModal,
      isStreamOrdering,
      handleBlockCaller,
      handleUnblockCaller,
      isCallerBlocked,
    ]
  );

  const renderFooter = () => {
    if (!hasNextPage) {
      return (
        <View className="py-4 flex items-center">
          <StyledText className="text-gray-500">
            There are no more calls{showOrdersOnly ? " with orders" : ""}.
          </StyledText>
        </View>
      );
    }
    return (
      <View className="py-4 flex items-center">
        <ActivityIndicator size="large" color="#6b7280" />
      </View>
    );
  };

  if (!activeAgent) return null;

  if (error) {
    return (
      <ErrorDisplay message="We're having trouble loading your call logs." />
    );
  }

  if (isSettingsLoading) {
    return <LoadingView />;
  }

  // Desktop layout
  if (isDesktop) {
    return (
      <PageTemplate title="Calls & Orders" headerIcon={Phone}>
        <TooltipProvider delayDuration={0}>
          <View className="flex-1 flex flex-row">
            <View className="flex-1 flex flex-col">
              <View className="p-2 bg-muted/25 border-b border-gray-200">
                <View className="flex-row items-center gap-4 mb-2">
                  <View className="flex-1">
                    <View className="flex-row items-center">
                      <View className="flex-row items-center flex-1 px-4 py-3 rounded-lg border border-gray-300 bg-white">
                        <Search size={18} color="#6B7280" />
                        <TextInput
                          className="flex-1 ml-3 text-gray-800 text-base"
                          placeholder="Search by phone number, caller name or call summary..."
                          placeholderTextColor="#9CA3AF"
                          value={searchQuery}
                          onChangeText={handleSearchQueryChange}
                          selectionColor="#000000"
                          cursorColor="#000000"
                        />
                        {searchQuery.length > 0 && (
                          <Pressable
                            className="h-[24px] w-[24px] flex items-center justify-center hover:bg-gray-100 rounded-full"
                            onPress={handleClearFilter}
                          >
                            <X size={16} color="#6B7280" />
                          </Pressable>
                        )}
                      </View>
                    </View>
                  </View>
                </View>

                <CallsFilter
                  onDateRangeChange={setDateRange}
                  onOrdersOnlyChange={setShowOrdersOnly}
                  dateRange={dateRange}
                  showOrdersOnly={showOrdersOnly}
                  isStreamOrdering={isStreamOrdering}
                />
              </View>

              {hasSearchQuery && callLogs.length === 0 && !isFetching && (
                <View className="bg-gray-50 border-b border-gray-200 px-6 py-4">
                  <StyledText className="text-sm text-gray-600">
                    No results found for "{debouncedSearchQuery}"
                  </StyledText>
                </View>
              )}

              {callLogs.length === 0 && !isFetching ? (
                <View className="flex-1 flex flex-col items-center p-6">
                  <StyledText className="text-sm text-gray-600">
                    No calls found.
                  </StyledText>
                </View>
              ) : (
                <div
                  ref={tableContainerRef}
                  className="flex-1 overflow-auto"
                  onScroll={handleTableScroll}
                >
                  <Table>
                    <TableHeader className="sticky top-0 bg-white z-10">
                      <TableRow>
                        <TableHead className="w-16 text-center"></TableHead>
                        <TableHead>Phone Number</TableHead>
                        <TableHead>Name</TableHead>
                        <TableHead>Time</TableHead>
                        {isStreamOrdering && (
                          <>
                            <TableHead>Items</TableHead>
                            <TableHead>Total</TableHead>
                            <TableHead>Type</TableHead>
                            <TableHead>Due</TableHead>
                          </>
                        )}
                        <TableHead>Duration</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {callLogs.map((call) => (
                        <TableRow
                          key={call.id}
                          className={`cursor-pointer hover:bg-gray-50 ${
                            selectedCallForPanel?.id === call.id &&
                            (isExpired(call) || isVoided(call)
                              ? "!bg-gray-50 border-l-4 !border-l-gray-400"
                              : "!bg-blue-50 border-l-4 border-l-blue-500")
                          } ${(isExpired(call) || isVoided(call)) && "!bg-gray-50/50 opacity-75"}`}
                          onClick={() => handleTableRowClick(call)}
                        >
                          <TableCell className="text-center">
                            <View className="flex items-center justify-center">
                              {isCallProcessing(call, isStreamOrdering) ? (
                                <ProcessingCallIcon />
                              ) : isCallWithOrder(call) ? (
                                <OrderCallIconWithStatus call={call} />
                              ) : (
                                <PhoneCallIcon call={call} />
                              )}
                            </View>
                          </TableCell>
                          <TableCell className="text-muted-foreground">
                            {formatPhoneNumber(call.phone)}
                          </TableCell>
                          <TableCell className="text-gray-700">
                            {call.customerName ? (
                              call.customerName
                            ) : (
                              <span className="text-neutral-400">N/A</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {isCallCompleted(call) ? (
                              <div>
                                <div className="text-gray-700">
                                  {formatTime(call.timestamp)}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {formatDateShort(call.timestamp)}
                                </div>
                              </div>
                            ) : isCallOngoing(call) ? (
                              <div className="h-9 flex items-center gap-1 animate-pulse">
                                <p className="text-sm text-blue-600">
                                  Call is ongoing
                                </p>
                                <Image
                                  source={liveIcon}
                                  className="w-5 h-5"
                                  style={{
                                    width: 20,
                                    height: 20,
                                    tintColor: "#2563eb",
                                  }}
                                  resizeMode="contain"
                                />
                              </div>
                            ) : null}
                          </TableCell>
                          {isStreamOrdering && (
                            <>
                              {isOrderSubmittedViaTablet(call) ? (
                                <>
                                  <TableCell colSpan={4}>
                                    <View className="flex-row items-center gap-1">
                                      <StyledText className="text-sm">
                                        See POS for Order Details
                                      </StyledText>
                                    </View>
                                  </TableCell>
                                </>
                              ) : isCallProcessing(call, isStreamOrdering) ? (
                                <>
                                  <TableCell colSpan={4}>
                                    <View className="flex-row items-center gap-1">
                                      <Badge
                                        variant="outline"
                                        className="bg-amber-50 text-amber-700 border-amber-200 gap-1 !cursor-pointer"
                                      >
                                        <StyledText>Processing...</StyledText>
                                      </Badge>
                                    </View>
                                  </TableCell>
                                </>
                              ) : call.order != null ? (
                                <>
                                  <TableCell>
                                    {call.order != null ? (
                                      !isSidePanelOpen ? (
                                        <Tooltip>
                                          <TooltipTrigger asChild>
                                            <span className="underline text-gray-700">
                                              {getItemCount(call.order.items)}
                                            </span>
                                          </TooltipTrigger>
                                          <TooltipContent className="max-w-xs border-2 border-gray-300 bg-white shadow-lg">
                                            <div className="text-sm leading-relaxed whitespace-pre-line">
                                              {callOrderItemsSummaryText(call)}
                                            </div>
                                          </TooltipContent>
                                        </Tooltip>
                                      ) : (
                                        <span className="text-gray-700">
                                          {getItemCount(call.order.items)}
                                        </span>
                                      )
                                    ) : (
                                      <span className="text-muted-foreground">
                                        -
                                      </span>
                                    )}
                                  </TableCell>
                                  <TableCell className="font-medium">
                                    {isExpired(call) ||
                                    hasBeenRefunded(call) ||
                                    isVoided(call) ? (
                                      <span className="text-gray-500">
                                        {displayPrice(call.order?.total ?? 0)}
                                      </span>
                                    ) : (
                                      displayPrice(call.order?.total ?? 0)
                                    )}
                                  </TableCell>
                                  <TableCell className="space-x-2 text-gray-700">
                                    {call.order.deliveryAddress != null ? (
                                      <p>Delivery</p>
                                    ) : (
                                      <p>Pickup</p>
                                    )}
                                  </TableCell>
                                  <TableCell className="text-gray-700">
                                    {call.order.futureOrderDate != null ? (
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <p className="underline">Scheduled</p>
                                        </TooltipTrigger>
                                        <TooltipContent>
                                          <p>
                                            Scheduled for{" "}
                                            {formatDateTime(
                                              call.order.futureOrderDate
                                            )}
                                          </p>
                                        </TooltipContent>
                                      </Tooltip>
                                    ) : (
                                      <p>ASAP</p>
                                    )}
                                  </TableCell>
                                </>
                              ) : (
                                <TableCell colSpan={4}></TableCell>
                              )}
                            </>
                          )}
                          <TableCell className="text-gray-700">
                            {formatDuration(call.duration)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>

                  {/* Loading indicator for desktop infinite scroll */}
                  {isFetchingNextPage && (
                    <div className="py-4 flex items-center justify-center">
                      <ActivityIndicator size="large" color="#6b7280" />
                    </div>
                  )}

                  {/* End of data indicator for desktop */}
                  {!hasNextPage && callLogs.length > 0 && (
                    <div className="py-4 flex items-center justify-center border-t border-gray-200">
                      <StyledText className="text-gray-500">
                        There are no more calls
                        {showOrdersOnly ? " with orders" : ""}.
                      </StyledText>
                    </div>
                  )}
                </div>
              )}
            </View>

            {/* Side Panel */}
            <div
              style={{
                position: "absolute",
                top: 0,
                right: 0,
                height: "100%",
                width: "24rem",
                pointerEvents: isSidePanelOpen ? "auto" : "none",
                zIndex: 50,
                transition: "transform 0.35s cubic-bezier(0.4, 0, 0.2, 1)",
                transform: isSidePanelOpen
                  ? "translateX(0)"
                  : "translateX(100%)",
                boxShadow: isSidePanelOpen
                  ? "-4px 0 24px 0 rgba(0,0,0,0.08)"
                  : undefined,
              }}
            >
              {(selectedCallForPanel || isPanelAnimating) && (
                <CallDetailsPanel
                  call={selectedCallForPanel!}
                  onClose={handleCloseSidePanel}
                  onViewHistory={handleFilterFromNumber}
                  onReportCall={handleOpenReportCallModal}
                  onRefund={handleOpenRefundModal}
                  onBlockCaller={(phone) =>
                    handleBlockCaller(selectedCallForPanel!.id, phone)
                  }
                  onUnblockCaller={(phone) =>
                    handleUnblockCaller(selectedCallForPanel!.id, phone)
                  }
                  isCallerBlocked={isCallerBlocked}
                  isBlockUnblockLoading={isBlockUnblockLoading}
                />
              )}
            </div>
          </View>

          <ReportCallModal
            visible={reportCallModalVisible}
            onClose={handleCloseReportCallModal}
            onSubmit={handleReportCallSubmit}
            callId={selectedCallId}
            callTimestamp={selectedCallTimestamp}
          />

          <RefundModal
            visible={refundModalVisible}
            onClose={handleCloseRefundModal}
            onSubmit={handleRefundSubmit}
            orderItems={selectedCallForRefund?.order?.items || []}
            orderTotal={selectedCallForRefund?.order?.total || 0}
            callerName={selectedCallForRefund?.customerName || ""}
            phone={selectedCallForRefund?.phone || ""}
            isLoading={refundLoading}
            showSuccess={refundSuccess}
            error={refundError}
          />
        </TooltipProvider>
      </PageTemplate>
    );
  }

  // mobile layout
  return (
    <PageTemplate title="Calls & Orders" headerIcon={Phone}>
      <View className="p-2 bg-[#FDFDFE] space-y-1.5 border-b border-border w-full max-w-2xl mx-auto">
        <View>
          <View className="flex-row items-center">
            <View className="flex-row items-center flex-1 px-4 py-2 rounded-md border border-border">
              <Search size={18} color="#6B7280" />
              <TextInput
                className="flex-1 ml-2.5 text-gray-800 text-base"
                placeholder="Search"
                placeholderTextColor="#9CA3AF"
                value={searchQuery}
                onChangeText={handleSearchQueryChange}
                selectionColor="#000000"
                cursorColor="#000000"
              />
              {searchQuery.length > 0 && (
                <Pressable
                  className="absolute right-1 top-1/2 -translate-y-1/2 p-2 hover:bg-gray-100 rounded-full"
                  onPress={handleClearFilter}
                >
                  <X size={16} className="text-muted-foreground" />
                </Pressable>
              )}
            </View>
          </View>
        </View>

        <CallsFilter
          onDateRangeChange={setDateRange}
          onOrdersOnlyChange={setShowOrdersOnly}
          dateRange={dateRange}
          showOrdersOnly={showOrdersOnly}
          isStreamOrdering={isStreamOrdering}
        />
      </View>

      {hasSearchQuery && callLogs.length === 0 && !isFetching && (
        <View className="bg-gray-50 border-b border-border px-4 py-3">
          <StyledText className="text-sm text-gray-600">
            No results found for "{debouncedSearchQuery}"
          </StyledText>
        </View>
      )}

      <FlatList
        ref={flatListRef}
        key={flatListKey}
        data={combinedData.items}
        renderItem={renderItem}
        keyExtractor={(item) => item.id}
        contentContainerStyle={{
          flexGrow: 1,
          paddingHorizontal: 8,
          paddingBottom: 32,
          paddingTop: 8,
          maxWidth: 672,
          marginHorizontal: "auto",
          width: "100%",
        }}
        onEndReached={handleLoadMore}
        onEndReachedThreshold={0.1}
        ListFooterComponent={callLogs.length ? renderFooter : null}
        ListEmptyComponent={
          !data && isFetching ? (
            <ActivityIndicator size="large" color="#6b7280" className="py-1" />
          ) : (
            <>
              {(hasActiveFilters || hasSearchQuery) &&
                callLogs.length === 0 && (
                  <View className="flex-1 items-center justify-center py-12 px-4">
                    <Phone size={48} color="#71717A" />
                    <StyledText className="text-lg font-medium my-2">
                      No calls found
                    </StyledText>
                    <StyledText className="text-muted-foreground mb-4">
                      Try adjusting your search or filters
                    </StyledText>
                  </View>
                )}
              {!hasActiveFilters &&
                !hasSearchQuery &&
                callLogs.length === 0 &&
                !isFetching && (
                  <View className="flex-1 items-center justify-center py-12 px-4">
                    <Phone size={48} color="#71717A" />
                    <StyledText className="text-lg font-medium my-2">
                      No calls found
                    </StyledText>
                    <StyledText className="text-muted-foreground mb-4">
                      Calls will appear here when you receive them
                    </StyledText>
                  </View>
                )}
            </>
          )
        }
      />

      <ReportCallModal
        visible={reportCallModalVisible}
        onClose={handleCloseReportCallModal}
        onSubmit={handleReportCallSubmit}
        callId={selectedCallId}
        callTimestamp={selectedCallTimestamp}
      />

      <RefundModal
        visible={refundModalVisible}
        onClose={handleCloseRefundModal}
        onSubmit={handleRefundSubmit}
        orderItems={selectedCallForRefund?.order?.items || []}
        orderTotal={selectedCallForRefund?.order?.total || 0}
        callerName={selectedCallForRefund?.customerName || ""}
        phone={selectedCallForRefund?.phone || ""}
        isLoading={refundLoading}
        showSuccess={refundSuccess}
        error={refundError}
      />
      <Trackbar />
    </PageTemplate>
  );
};

export default CallsAndOrders;
